import React, {
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  Eye,
  EyeSlash,
  Key,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import { mail as mailApi } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatApiError } from "@/lib/api";
import MailRulesDialog from "@/components/mail/MailRulesDialog";
import MailSignatureDialog from "@/components/mail/MailSignatureDialog";


const MAIL_SETTINGS_TABS = [
  { id: "account", label: "Compte" },
  { id: "rules", label: "Règles" },
  { id: "signature", label: "Signature" },
];


export default function MyMailSettings() {
  const [
    activeTab,
    setActiveTab,
  ] = useState("account");

  const [
    accountState,
    setAccountState,
  ] = useState({
    loading: true,
    configured: false,
    hasPendingConfiguration: false,
    managedConfiguration: false,
    account: null,
  });

  const [
    password,
    setPassword,
  ] = useState("");

  const [
    showPassword,
    setShowPassword,
  ] = useState(false);

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    folders,
    setFolders,
  ] = useState([]);

  const [
    signatureText,
    setSignatureText,
  ] = useState("");

  const [
    signatureAutoEnabled,
    setSignatureAutoEnabled,
  ] = useState(false);

  const [
    signatureLoading,
    setSignatureLoading,
  ] = useState(false);

  const [
    signatureSaving,
    setSignatureSaving,
  ] = useState(false);

  const loadAccount = useCallback(
    async () => {
      try {
        const response =
          await mailApi.account();

        setAccountState({
          loading: false,
          configured: Boolean(
            response?.configured
          ),
          hasPendingConfiguration:
            Boolean(
              response?.hasPendingConfiguration
            ),
          managedConfiguration:
            Boolean(
              response?.managedConfiguration
            ),
          account:
            response?.account || null,
        });
      } catch (error) {
        setAccountState({
          loading: false,
          configured: false,
          hasPendingConfiguration: false,
          managedConfiguration: false,
          account: null,
        });

        toast.error(
          formatApiError(
            error?.response?.data?.detail
          )
          || "Impossible de charger la configuration de messagerie."
        );
      }
    },
    []
  );

  useEffect(
    () => {
      loadAccount();
    },
    [loadAccount]
  );

  const savePassword =
    async () => {
      if (!password) {
        toast.error(
          "Le mot de passe de messagerie est obligatoire."
        );
        return;
      }

      setSaving(true);

      try {
        await mailApi.configureAccount({
          password,
        });

        await mailApi.testAccount();

        setPassword("");
        setShowPassword(false);

        await loadAccount();

        toast.success(
          "Mot de passe de messagerie validé."
        );
      } catch (error) {
        setPassword("");

        toast.error(
          formatApiError(
            error?.response?.data?.detail
          )
          || "Connexion à la boîte mail impossible."
        );
      } finally {
        setSaving(false);
      }
    };

  useEffect(
    () => {
      if (activeTab !== "rules") {
        return;
      }

      mailApi
        .listFolders()
        .then((response) => {
          setFolders(
            response?.items || []
          );
        })
        .catch((error) => {
          toast.error(
            formatApiError(
              error?.response?.data?.detail
            )
            || "Impossible de charger les dossiers."
          );
        });
    },
    [activeTab]
  );

  useEffect(
    () => {
      if (activeTab !== "signature") {
        return;
      }

      setSignatureLoading(true);

      mailApi
        .getSignature()
        .then((response) => {
          setSignatureText(
            response?.text || ""
          );
          setSignatureAutoEnabled(
            Boolean(
              response?.autoEnabled
            )
          );
        })
        .catch((error) => {
          toast.error(
            formatApiError(
              error?.response?.data?.detail
            )
            || "Impossible de charger la signature"
          );
        })
        .finally(() => {
          setSignatureLoading(false);
        });
    },
    [activeTab]
  );

  const saveSignature =
    async (
      text,
      autoEnabled
    ) => {
      if (signatureSaving) {
        return;
      }

      setSignatureSaving(true);

      try {
        const response =
          await mailApi.saveSignature(
            text,
            autoEnabled
          );

        setSignatureText(
          response?.text || ""
        );
        setSignatureAutoEnabled(
          Boolean(
            response?.autoEnabled
          )
        );

        toast.success(
          "Signature enregistrée"
        );
      } catch (error) {
        toast.error(
          formatApiError(
            error?.response?.data?.detail
          )
          || "Impossible d’enregistrer la signature"
        );
      } finally {
        setSignatureSaving(false);
      }
    };

  const account =
    accountState.account;

  if (accountState.loading) {
    return (
      <div
        className="label-eyebrow lirah-pulse"
        data-testid="my-mail-settings-loading"
      >
        Chargement
      </div>
    );
  }

  return (
    <div
      className="space-y-4"
      data-testid="settings-mail-account-panel"
    >
      <div
        className="flex flex-wrap gap-2 border-b border-[var(--do-border)] pb-3"
        role="tablist"
        aria-label="Configuration de ma messagerie"
        data-testid="mail-settings-tabs"
      >
        {MAIL_SETTINGS_TABS.map(
          (tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={
                activeTab === tab.id
              }
              onClick={() =>
                setActiveTab(
                  tab.id
                )
              }
              className={[
                "rounded-lg px-4 py-2 text-xs font-heading font-bold transition",
                activeTab === tab.id
                  ? "bg-[var(--do-accent-soft)] text-[var(--do-accent)]"
                  : "text-[var(--do-muted)] hover:bg-[var(--do-bg-2)] hover:text-[var(--do-ink)]",
              ].join(" ")}
              data-testid={
                `mail-settings-tab-${tab.id}`
              }
            >
              {tab.label}
            </button>
          )
        )}
      </div>

      {activeTab === "account" && (
        <section
          className="lirah-card settings-section-card p-4 sm:p-6"
          data-testid="mail-settings-account"
        >
          <div>
            <h2 className="font-heading text-base font-black text-[var(--do-ink)]">
              Compte de messagerie
            </h2>
            <p className="mt-1 text-xs text-[var(--do-muted)]">
              {accountState.managedConfiguration
                ? "Votre adresse @almafrance.fr est configurée automatiquement. Saisissez uniquement le mot de passe de votre boîte."
                : "Les paramètres techniques sont gérés par votre administrateur. Vous gérez uniquement le mot de passe de votre propre boîte."}
            </p>
          </div>

          {!account ? (
            <div className="mt-5 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4 text-sm text-[var(--do-muted)]">
              Aucune configuration de messagerie n’a encore été préparée. Contactez votre administrateur.
            </div>
          ) : (
            <div className="mt-5 space-y-5">
              <dl className="grid gap-3 text-xs sm:grid-cols-2">
                <div>
                  <dt className="text-[var(--do-muted)]">Adresse e-mail</dt>
                  <dd className="mt-1 font-semibold text-[var(--do-ink)]">{account.email || "—"}</dd>
                </div>
                <div>
                  <dt className="text-[var(--do-muted)]">État</dt>
                  <dd className="mt-1 font-semibold text-[var(--do-ink)]">
                    {accountState.configured
                      ? "Messagerie connectée"
                      : accountState.hasPendingConfiguration
                        ? "À connecter"
                        : "Non configurée"}
                  </dd>
                </div>

                {!accountState.managedConfiguration && (
                  <>
                    <div>
                      <dt className="text-[var(--do-muted)]">IMAP</dt>
                      <dd className="mt-1 font-semibold text-[var(--do-ink)]">{account.imapHost || "—"}:{account.imapPort || "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-[var(--do-muted)]">SMTP</dt>
                      <dd className="mt-1 font-semibold text-[var(--do-ink)]">{account.smtpHost || "—"}:{account.smtpPort || "—"}</dd>
                    </div>
                  </>
                )}
              </dl>

              <label className="block">
                <span className="text-xs font-semibold text-[var(--do-ink)]">
                  {accountState.managedConfiguration
                    ? "Mot de passe de votre boîte mail"
                    : "Nouveau mot de passe"}
                </span>
                <div className="relative mt-2">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    autoComplete="new-password"
                    disabled={saving}
                    onChange={(event) =>
                      setPassword(
                        event.target.value
                      )
                    }
                    className="pr-10"
                    data-testid="mail-own-password-input"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword(
                        (value) => !value
                      )
                    }
                    disabled={saving}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--do-muted)] disabled:opacity-50"
                    aria-label={
                      showPassword
                        ? "Masquer le mot de passe"
                        : "Afficher le mot de passe"
                    }
                  >
                    {showPassword ? <EyeSlash size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </label>

              <Button
                type="button"
                variant="actaPrimary"
                disabled={saving || !password}
                onClick={savePassword}
                data-testid="mail-own-password-save"
              >
                <Key size={16} weight="bold" />
                {saving
                  ? "Test en cours…"
                  : accountState.managedConfiguration
                    ? (
                        accountState.configured
                          ? "Modifier le mot de passe"
                          : "Connecter ma messagerie"
                      )
                    : "Enregistrer et tester"}
              </Button>
            </div>
          )}
        </section>
      )}

      {activeTab === "rules" && (
        <section
          className="lirah-card settings-section-card p-4 sm:p-6"
          data-testid="mail-settings-rules"
        >
          <h2 className="font-heading text-base font-black text-[var(--do-ink)]">
            Règles de classement
          </h2>
          <div className="mt-5">
            <MailRulesDialog
              open
              embedded
              folders={folders}
            />
          </div>
        </section>
      )}

      {activeTab === "signature" && (
        <section
          className="lirah-card settings-section-card p-4 sm:p-6"
          data-testid="mail-settings-signature"
        >
          <h2 className="font-heading text-base font-black text-[var(--do-ink)]">
            Signature
          </h2>
          <div className="mt-5">
            {signatureLoading ? (
              <div className="label-eyebrow lirah-pulse">
                Chargement
              </div>
            ) : (
              <MailSignatureDialog
                open
                embedded
                initialValue={signatureText}
                initialAutoEnabled={signatureAutoEnabled}
                saving={signatureSaving}
                onSubmit={saveSignature}
              />
            )}
          </div>
        </section>
      )}
    </div>
  );

}
